import React, { useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import ShareRow from '../../components/ShareRow/ShareRow';
import BirthFields, { birthPayload, emptyBirth } from '../../components/BirthFields/BirthFields';
import { chartDateHref } from '../../components/DateFields/DateFields';
import { venusSign } from '../../services/chartService';
import { SIGNS, SIGN_BY_NAME } from '../../data/astro/signs';
import { VENUS_RETROGRADE, VENUS_SIGNS } from '../../data/astro/venusSigns';
import '../Astro/Astro.css';
import './VenusSign.css';

const FAQ = [
  {
    question: 'What is a Venus sign?',
    answer: 'Your Venus sign is the zodiac sign Venus was in when you were born. It describes how you love, what attracts you, what you value, and your sense of beauty and pleasure.',
  },
  {
    question: 'Do I need my birth time to find my Venus sign?',
    answer: 'Usually not. Venus stays in a sign for about four weeks, so the date is almost always enough. On the few days Venus changes signs, we show both options; adding your birth time and place settles it.',
  },
  {
    question: 'Is my Venus sign the same as my sun sign?',
    answer: 'Often it\'s the same or one sign either side, because Venus never moves more than 48 degrees from the Sun. That is why many people have Venus in their sun sign or a neighbouring one.',
  },
  {
    question: 'Does my Venus sign show who I\'m compatible with?',
    answer: 'It shows what you\'re drawn to and what you need in love. For a real compatibility reading, compare both birth charts, which looks at Venus, Mars, the Moon, and much more.',
  },
  {
    question: 'What does Venus retrograde in a birth chart mean?',
    answer: 'About 7% of people are born with Venus retrograde. It is often read as an inward, reflective approach to love and self-worth, with a tendency to revisit the past and value what lasts.',
  },
];

const SECTIONS = [
  ['love', 'How you love'],
  ['attracts', 'What attracts you'],
  ['needs', 'What you need back'],
  ['style', 'Your style and taste'],
  ['shadow', 'Watch for'],
  ['tip', 'Love advice'],
];

function VenusResult({ sign }) {
  const info = VENUS_SIGNS[sign];
  return (
    <div className="venus-sign-body">
      <p className="venus-headline">{info.headline}</p>
      <div className="venus-grid">
        {SECTIONS.map(([key, title]) => (
          <article key={key} className="astro-card">
            <h3>{title}</h3>
            <p>{info[key]}</p>
          </article>
        ))}
      </div>
      <p className="venus-best">
        <strong>Often clicks with Venus in:</strong>{' '}
        {info.bestWith.map((name, index) => (
          <React.Fragment key={name}>
            {index > 0 && ', '}
            <a href={`#venus-${SIGN_BY_NAME[name].slug}`}>{name}</a>
          </React.Fragment>
        ))}
      </p>
    </div>
  );
}

const VenusSign = () => {
  const [birth, setBirth] = useState(() => emptyBirth());
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const resultRef = useRef(null);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const payload = birthPayload(birth, { requirePlace: false });
    if (payload.error) {
      setError(payload.error.charAt(0).toUpperCase() + payload.error.slice(1));
      return;
    }
    setError('');
    setBusy(true);
    try {
      const { year, month, day, hour, minute, latitude, longitude, timezone } = payload;
      setResult(await venusSign({ year, month, day, hour, minute, latitude, longitude, timezone }));
      setTimeout(() => resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 30);
    } catch (err) {
      setError(err.message || 'Venus could not be calculated. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const sign = result?.sign;
  const glyph = sign ? SIGN_BY_NAME[sign]?.glyph : '';
  const shareText = sign
    ? `My Venus is in ${sign}: ${VENUS_SIGNS[sign].headline} Find your Venus sign, free:`
    : 'Find your Venus sign, free:';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebApplication',
        name: 'Venus Sign Calculator',
        url: 'https://nowtarot.com/venus-sign-calculator/',
        applicationCategory: 'LifestyleApplication',
        offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
        description: 'Free Venus sign calculator. Find your Venus sign from your birthday and learn how you love, what attracts you, and what you need in a relationship.',
      },
      {
        '@type': 'FAQPage',
        mainEntity: FAQ.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: { '@type': 'Answer', text: item.answer },
        })),
      },
    ],
  };

  return (
    <div className="astro-page">
      <Helmet>
        <title>Venus Sign Calculator: How You Love | NowTarot</title>
        <meta name="description" content="Free Venus sign calculator. Enter your birthday to find your Venus sign and what it says about how you love, what attracts you, and what you need from a partner." />
        <link rel="canonical" href="https://nowtarot.com/venus-sign-calculator/" />
        <meta property="og:title" content="Venus Sign Calculator: How You Love | NowTarot" />
        <meta property="og:description" content="Find your Venus sign and what it says about how you love." />
        <meta property="og:url" content="https://nowtarot.com/venus-sign-calculator/" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>
      <Header />
      <main>
        <p className="astro-eyebrow">Venus sign calculator · Free</p>
        <h1>What is your <em>Venus</em> sign?</h1>
        <p className="astro-lede">
          Venus shows how you love: what draws you to someone, how you show affection, and what you need back.
          Your birthday is usually enough. Add a time and place if you have them.
        </p>

        <form className="astro-card venus-form" onSubmit={handleSubmit}>
          <BirthFields value={birth} onChange={setBirth} placeOptional timeOptional />
          <button className="astro-button" type="submit" disabled={busy}>
            {busy ? 'Finding Venus…' : 'Find my Venus sign'}
          </button>
          {error && <p className="astro-error">{error}</p>}
        </form>

        {result && (
          <section className="astro-section venus-result" ref={resultRef} aria-live="polite">
            {result.change ? (
              <>
                <p className="astro-eyebrow">Your Venus sign</p>
                <h2>Venus in {result.change.from} or {result.change.to}</h2>
                <p>
                  Venus moved from {result.change.from} into {result.change.to} on your birthday. Add your birth time
                  and birthplace above to find out which one is yours, or read both and see which fits.
                </p>
                <h3 className="venus-option">Venus in {result.change.from}</h3>
                <VenusResult sign={result.change.from} />
                <h3 className="venus-option">Venus in {result.change.to}</h3>
                <VenusResult sign={result.change.to} />
              </>
            ) : (
              <>
                <p className="astro-eyebrow">Your Venus sign</p>
                <h2><span className="astro-glyph" aria-hidden="true">{glyph}</span> Venus in {sign}</h2>
                {result.exact && result.degree && <p className="astro-muted">{result.degree} {sign}</p>}
                <VenusResult sign={sign} />
              </>
            )}
            {result.retrograde && <p className="venus-retro"><strong>Venus retrograde.</strong> {VENUS_RETROGRADE}</p>}
            <ShareRow url="/venus-sign-calculator/" text={shareText} label="Share your Venus sign" />
            <div className="astro-next">
              <Link className="astro-next-lead" to={chartDateHref(birth)}>
                <strong>Your birth chart</strong>
                <span>Venus is one planet. Add your birth time and place to see them all, with the houses and a reading you can ask about.</span>
              </Link>
              <Link to="/birth-chart-compatibility/">
                <strong>Compare two birth charts</strong>
                <span>See how your Venus, Mars, and Moon fit with someone else's.</span>
              </Link>
              <Link to="/zodiac-compatibility/">
                <strong>Zodiac compatibility</strong>
                <span>How your sun sign matches with all twelve signs.</span>
              </Link>
            </div>
          </section>
        )}

        <section className="astro-section">
          <h2>What your Venus sign <em>means</em></h2>
          <p>
            In astrology, Venus is the planet of love, beauty, pleasure, and value. Your Venus sign is where Venus was
            when you were born, and it colors how you give and receive affection. Your sun sign says who you are; your
            Venus sign says how you love.
          </p>
          <p>
            Venus spends about four weeks in each sign and never strays more than two signs from the Sun. So your Venus
            sign is often your sun sign or one of its neighbours. When it differs, it explains a lot: a fiery Aries Sun
            with a gentle Pisces Venus loves very differently from what the sun sign alone would suggest.
          </p>
          <p>
            Venus also shows what you find beautiful and what you value, from the clothes you reach for to the way you
            spend money and time. In a relationship, comparing two people's Venus and Mars signs is one of the first
            things astrologers do.
          </p>
        </section>

        <section className="astro-section">
          <h2>Venus in each sign</h2>
          <div className="venus-sign-list">
            {SIGNS.map((item) => (
              <article key={item.slug} id={`venus-${item.slug}`} className="astro-card">
                <h3><span aria-hidden="true">{item.glyph}</span> Venus in {item.name}</h3>
                <p><strong>{VENUS_SIGNS[item.name].headline}</strong></p>
                <p>{VENUS_SIGNS[item.name].love}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="astro-section astro-faq">
          <h2>Venus sign questions</h2>
          {FAQ.map((item) => (
            <article key={item.question}>
              <h3>{item.question}</h3>
              <p>{item.answer}</p>
            </article>
          ))}
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default VenusSign;
